import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Monitor, Smartphone, Wifi, Mail, Settings } from 'lucide-react';
import { siteConfig } from '../../data';
import ContactSection from '../../components/ContactSection';

export default function TekniskHjaelp() {
  return (
    <div className="bg-white">
      <Helmet>
        <title>Teknisk hjælp | IT-support til ældre | Online Tryghed</title>
        <meta name="description" content="Har du problemer med computeren, tabletten eller internettet? Vi yder tålmodig teknisk hjælp i dit eget hjem." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative py-20 bg-slate-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-[#103B66] mb-6 leading-tight">
              Teknisk hjælp: Vi får teknikken til at spille
            </h1>
            <p className="text-xl text-slate-600 mb-8 leading-relaxed">
              Teknologi skal gøre livet lettere, ikke sværere. Vi hjælper dig med alt fra opsætning af ny iPad til fejlfinding på dit trådløse netværk.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href={`tel:${siteConfig.phone.replace(/\s/g, '')}`} className="px-8 py-4 bg-[#103B66] text-white font-bold rounded-sm hover:bg-slate-800 transition-all">
                Få teknisk support
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-[#103B66] mb-4">Hvad kan vi hjælpe med?</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">Ingen opgave er for lille. Vi hjælper med alle de daglige tekniske udfordringer.</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: Monitor,
                title: "Computer & Tablet",
                desc: "Hjælp til Windows, Mac, iPad og Android tablets. Vi rydder op og gør den hurtigere."
              },
              {
                icon: Smartphone,
                title: "Mobiltelefon",
                desc: "Opsætning af apps, overførsel af billeder og hjælp til at bruge telefonen optimalt."
              },
              {
                icon: Wifi,
                title: "Internet & WiFi",
                desc: "Vi sikrer, at dit internet dækker hele boligen og at sikkerheden er i top."
              },
              {
                icon: Mail,
                title: "Mail & Kommunikation",
                desc: "Opsætning af e-mail, Skype, WhatsApp eller FaceTime, så du kan holde kontakten."
              },
              {
                icon: Settings,
                title: "Printer & Tilbehør",
                desc: "Installation af printere, scannere og andet udstyr, så det bare virker."
              },
              {
                icon: Lock,
                title: "MitID & Sikkerhed",
                desc: "Hjælp til MitID, Digital Post og sikring af dine personlige oplysninger."
              }
            ].map((item, i) => (
              <div key={i} className="p-8 bg-white border border-slate-100 rounded-none hover:shadow-md transition-shadow">
                <item.icon className="w-10 h-10 text-black mb-6" />
                <h3 className="text-xl font-bold text-[#103B66] mb-3">{item.title}</h3>
                <p className="text-slate-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-[#103B66] rounded-none overflow-hidden shadow-2xl">
            <div className="grid md:grid-cols-2">
              <div className="p-12 md:p-16 text-white">
                <h2 className="text-3xl font-bold mb-6">Hjemmebesøg eller fjernsupport?</h2>
                <p className="text-blue-100 text-lg mb-8">
                  Nogle problemer løses bedst, når vi sidder sammen ved din computer. Andre kan vi hurtigt fikse over telefonen eller via fjernstyring. Vi finder den løsning, der passer dig bedst.
                </p>
                <div className="space-y-4">
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-green-400 rounded-none mr-3"></div>
                    <span>Telefon & fjernsupport: 249,- pr. halve time</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-green-400 rounded-none mr-3"></div>
                    <span>Personligt fremmøde: 349,- pr. halve time</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-green-400 rounded-none mr-3"></div>
                    <span>Kørsel: 10,- pr. km (kun udrejsen fra Slagelse)</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-green-400 rounded-none mr-3"></div>
                    <span>Alle priser er inklusiv moms</span>
                  </div>
                </div>
              </div>
              <div className="bg-blue-600/20 flex items-center justify-center p-12">
                <div className="text-center">
                  <div className="text-5xl font-bold text-white mb-2">249,-</div>
                  <div className="text-blue-100 uppercase tracking-wider font-semibold">Fra kun</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ContactSection />
    </div>
  );
}

function Lock(props) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  )
}
